import { useRoute } from "@react-navigation/core";
import { useMemo } from "react";
import { ScreenHeaderFilters } from "../screens/user/ScreenHeaderFilters";

export type FilterOption<T extends string> = {
    label: string;
    quantity: number;
    value: T;
    checked: boolean;
};

type UseTypeFiltersOptions<TItem, TFilterValue extends string> = {
    items: TItem[] | undefined;
    getFilterValue: (item: TItem) => TFilterValue;
    defaultFilterValues?: TFilterValue[];
    getLabel: (value: TFilterValue, quantity: number) => string;
    sortOrder?: TFilterValue[];
};

export const useTypeFilters = <TItem, TFilterValue extends string>({
    items,
    getFilterValue,
    defaultFilterValues = items?.map((item) => getFilterValue(item)) ?? [],
    getLabel,
    sortOrder,
}: UseTypeFiltersOptions<TItem, TFilterValue>) => {
    const { params: { filters = defaultFilterValues?.join(",") } = {} } = useRoute() as {
        params?: { filters?: string };
    };

    // Callers usually build `defaultFilterValues` inline, or let it default to the items' own
    // values: both yield a fresh array on every render. Re-derive a content-stable array from a
    // primitive key so the memos below only recompute when the defaults actually change.
    const defaultValuesKey = defaultFilterValues.join(",");
    const defaultValues = useMemo(
        () => defaultValuesKey.split(",").filter(Boolean) as TFilterValue[],
        [defaultValuesKey],
    );

    // `filter(Boolean)` drops the empty entry an emptied "filters" param would otherwise produce
    // ("".split(",") === [""]), which must read as "no selection", not as a filter named "".
    const activeFilterValues = useMemo(() => filters?.split(",").filter(Boolean) ?? [], [filters]);

    const availableFilterOptions = useMemo<FilterOption<TFilterValue>[]>(() => {
        if (!items) return [];

        const counts = items.reduce<Partial<Record<TFilterValue, number>>>((result, item) => {
            const value = getFilterValue(item);
            return { ...result, [value]: (result[value] ?? 0) + 1 };
        }, {});

        const entries = (Object.entries(counts) as [TFilterValue, number][]).filter(
            ([, quantity]) => quantity > 0,
        );

        // At least one filter always stays on (BAS-2875). A selection matching none of the
        // available options — an emptied or stale "filters" param, or an option that vanished on
        // refetch (the last draft got signed) — would filter every item out and show the "nothing
        // here" empty state while items actually exist. Fall back to the default selection, then
        // to everything, so the list can never end up hiding all of its content.
        const selectedValues = entries
            .map(([value]) => value)
            .filter((value) => activeFilterValues.includes(value));
        const fallbackValues = entries
            .map(([value]) => value)
            .filter((value) => defaultValues.includes(value));
        const effectiveValues = new Set(
            selectedValues.length > 0
                ? selectedValues
                : fallbackValues.length > 0
                  ? fallbackValues
                  : entries.map(([value]) => value),
        );

        const options = entries.map(([value, quantity]) => ({
            label: getLabel(value, quantity),
            quantity,
            value,
            checked: effectiveValues.has(value),
        }));

        if (sortOrder) {
            options.sort((a, b) => {
                const aIndex = sortOrder.indexOf(a.value);
                const bIndex = sortOrder.indexOf(b.value);
                const aOrder = aIndex === -1 ? sortOrder.length : aIndex;
                const bOrder = bIndex === -1 ? sortOrder.length : bIndex;
                return aOrder - bOrder;
            });
        }

        return options;
    }, [items, getFilterValue, getLabel, sortOrder, activeFilterValues, defaultValues]);

    const activeFilters = useMemo(
        () => availableFilterOptions.filter((option) => option.checked),
        [availableFilterOptions],
    );

    const filteredItems = useMemo(
        () => items?.filter((item) => activeFilters?.find((f) => f.value === getFilterValue(item))),
        [items, activeFilters, getFilterValue],
    );

    const filterComponent = useMemo(() => {
        return availableFilterOptions.length > 0 ? (
            <ScreenHeaderFilters<TFilterValue>
                activeFilters={availableFilterOptions}
                defaultActiveFiltersValues={defaultValues}
            />
        ) : null;
    }, [availableFilterOptions, defaultValues]);

    return { filteredItems, filterComponent, availableFilterOptions, activeFilters };
};
